import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  Users,
  Mail, 
  Phone, 
  CheckCircle, 
  XCircle, 
  MessageCircle,
  BookOpen,
  Star,
  AlertCircle,
  Eye,
  Check,
  X,
  Plus,
  Settings,
  FileText,
  IndianRupee,
  CreditCard,
  Save,
  Share2,
  Copy,
  Gift
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { demoBookingService, teacherHiringService } from '../firebase/tuition';
import { paymentManagementService } from '../firebase/paymentManagement';
import { useRealtime } from '../contexts/RealtimeContext';
import { updateUserProfile } from '../firebase/collections';
import { referralService } from '../firebase/referralService';
import PaymentModal from './payment/PaymentModal';
import TeacherBatchManagement from './TeacherBatchManagement';

const TeacherDashboardMinimal = () => {
  const { currentUser: user, userProfile } = useRealtime();
  const [demoBookings, setDemoBookings] = useState([]);
  const [hiredStudents, setHiredStudents] = useState([]);
  const [teacherBatches, setTeacherBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('demo-bookings');
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDescription, setNewBatchDescription] = useState('');
  const [showBatchManagement, setShowBatchManagement] = useState(false);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    email: '',
    phone: '',
    businessType: 'individual',
    businessName: '',
    pan: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [batchLimit, setBatchLimit] = useState(3);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0
  });
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    if (user) {
      loadAllData();
    } else {
    }
  }, [user]);

  useEffect(() => {
    // Load bank details if they exist
    if (userProfile?.bankDetails) {
      setBankDetails({
        accountHolderName: userProfile.bankDetails.accountHolderName || '',
        accountNumber: userProfile.bankDetails.accountNumber || '',
        ifscCode: userProfile.bankDetails.ifscCode || '',
        bankName: userProfile.bankDetails.bankName || '',
        email: userProfile.bankDetails.email || userProfile?.email || '',
        phone: userProfile.bankDetails.phone || userProfile?.phone || '',
        businessType: userProfile.bankDetails.businessType || 'individual',
        businessName: userProfile.bankDetails.businessName || userProfile?.displayName || '',
        pan: userProfile.bankDetails.pan || userProfile?.pan || '',
        street1: userProfile.bankDetails.street1 || userProfile?.address?.street || '',
        street2: userProfile.bankDetails.street2 || '',
        city: userProfile.bankDetails.city || userProfile?.address?.city || '',
        state: userProfile.bankDetails.state || userProfile?.address?.state || '',
        pincode: userProfile.bankDetails.pincode || userProfile?.address?.pincode || ''
      });
    } else if (userProfile) {
      setBankDetails(prev => ({
        ...prev,
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        businessName: userProfile.displayName || '',
        pan: userProfile.pan || '',
        street1: userProfile.address?.street || '',
        city: userProfile.address?.city || '',
        state: userProfile.address?.state || '',
        pincode: userProfile.address?.pincode || ''
      }));
    }
  }, [userProfile]);

  const loadAllData = async () => {
    await Promise.all([
      loadDemoBookings(),
      loadHiredStudents(),
      loadTeacherBatches(),
      loadEarnings(),
      loadBatchLimit(),
      loadReferralData()
    ]);
  };

  const loadBatchLimit = async () => {
    if (user) {
      try {
        const limit = await teacherHiringService.getTeacherBatchLimit(user.uid);
        setBatchLimit(limit || 3);
      } catch (error) {
        // Default to 3 if error
        setBatchLimit(3);
      }
    } else {
      setBatchLimit(3);
    }
  };

  const loadDemoBookings = async () => {
    try {
      const bookings = await demoBookingService.getTeacherDemoBookings(user.uid);
      setDemoBookings(bookings);
    } catch (error) {
      toast.error('Failed to load demo bookings');
    }
  };

  const loadHiredStudents = async () => {
    try {
      
      const students = await teacherHiringService.getTeacherHiredStudents(user.uid);
      
      setHiredStudents(students);
    } catch (error) {
      toast.error('Failed to load hired students');
    }
  };

  const loadTeacherBatches = async () => {
    try {
      const batches = await teacherHiringService.getTeacherBatches(user.uid);
      setTeacherBatches(batches);
    } catch (error) {
      toast.error('Failed to load teacher batches');
    } finally {
      setLoading(false);
    }
  };

  const loadEarnings = async () => {
    try {
      const result = await paymentManagementService.getTeacherEarnings(user.uid);
      if (result.success) {
        setTotalEarnings(result.totalEarnings || 0);
      }
    } catch (error) {
      // Don't show error toast for earnings, just set to 0
      setTotalEarnings(0);
    }
  };

  const loadReferralData = async () => {
    if (!user) return;
    try {
      const stats = await referralService.getReferralStats(user.uid);
      if (stats.success) {
        setReferralCode(stats.referralCode);
        setReferralLink(stats.referralLink);
        setReferralStats({
          totalReferrals: stats.totalReferrals || 0,
          totalEarnings: stats.totalEarnings || 0
        });
      }

      // Load referral list
      const referralsResult = await referralService.getTeacherReferrals(user.uid);
      if (referralsResult.success) {
        console.log('Loaded referrals:', referralsResult.referrals);
        setReferrals(referralsResult.referrals || []);
      } else {
        console.error('Failed to load referrals:', referralsResult.error);
        setReferrals([]);
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
      setReferrals([]);
    }
  };

  const handleCopyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const handleCopyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied to clipboard!');
  };

  const handleShareOnWhatsApp = () => {
    const message = `Join Mahstry as a teacher and start earning! When you register as a tuition teacher, use my referral code: ${referralCode}\n\nSign up here: ${referralLink}\n\nAfter signing up, register as a teacher and enter the referral code during registration to earn me ₹10!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleConfirmBooking = async (bookingId, selectedSlotIndex) => {
    setIsProcessing(true);
    try {
      await demoBookingService.confirmDemoBooking(bookingId, selectedSlotIndex);
      toast.success('Demo booking confirmed successfully!');
      loadDemoBookings();
      setShowBookingDetails(false);
    } catch (error) {
      toast.error('Failed to confirm booking');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectBooking = async (bookingId) => {
    setIsProcessing(true);
    try {
      await demoBookingService.updateDemoBookingStatus(bookingId, 'cancelled');
      toast.success('Demo booking rejected');
      loadDemoBookings();
      setShowBookingDetails(false);
    } catch (error) {
      toast.error('Failed to reject booking');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  const handleCreateBatch = async () => {
    if (!newBatchName.trim()) {
      toast.error('Please enter a batch name');
      return;
    }

    setIsProcessing(true);
    try {
      
      await teacherHiringService.createTeacherBatch(user.uid, {
        batchName: newBatchName.trim(),
        description: newBatchDescription.trim(),
        studentCount: 0
      });
      toast.success('Batch created successfully!');
      setNewBatchName('');
      setNewBatchDescription('');
      setShowCreateBatch(false);
      loadTeacherBatches();
      loadBatchLimit();
    } catch (error) {
      toast.error(error.message || 'Failed to create batch');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'confirmed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'confirmed':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return 'N/A';
    const dateObj = new Date(date);
    return `${dateObj.toLocaleDateString()} at ${time}`;
  };

  const handleSaveBankDetails = async () => {
    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    // Validate required fields
    if (!bankDetails.accountHolderName.trim() || !bankDetails.accountNumber.trim() || 
        !bankDetails.ifscCode.trim() || !bankDetails.bankName.trim() ||
        !bankDetails.email.trim() || !bankDetails.phone.trim() ||
        !bankDetails.businessName.trim() || !bankDetails.street1.trim() ||
        !bankDetails.city.trim() || !bankDetails.state.trim() || !bankDetails.pincode.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate IFSC code format (11 characters, alphanumeric)
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankDetails.ifscCode.toUpperCase())) {
      toast.error('Invalid IFSC code format');
      return;
    }

    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bankDetails.email)) {
      toast.error('Invalid email format');
      return;
    }

    // Validate phone (10 digits)
    if (!/^\d{10}$/.test(bankDetails.phone)) {
      toast.error('Phone number must be 10 digits');
      return;
    }

    // Validate pincode (6 digits)
    if (!/^\d{6}$/.test(bankDetails.pincode)) {
      toast.error('Pincode must be 6 digits');
      return;
    }

    // Validate PAN (10 characters: 5 letters, 4 digits, 1 letter)
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(bankDetails.pan.toUpperCase())) {
      toast.error('Invalid PAN format (e.g., ABCDE1234F)');
      return;
    }

    setSavingBankDetails(true);
    try {
      const result = await updateUserProfile(user.uid, {
        bankDetails: {
          accountHolderName: bankDetails.accountHolderName.trim(),
          accountNumber: bankDetails.accountNumber.trim(),
          ifscCode: bankDetails.ifscCode.toUpperCase().trim(),
          bankName: bankDetails.bankName.trim(),
          email: bankDetails.email.trim(),
          phone: bankDetails.phone.trim(),
          businessType: bankDetails.businessType,
          businessName: bankDetails.businessName.trim(),
          pan: bankDetails.pan.toUpperCase().trim(),
          street1: bankDetails.street1.trim(),
          street2: bankDetails.street2.trim(),
          city: bankDetails.city.trim(),
          state: bankDetails.state.trim(),
          pincode: bankDetails.pincode.trim()
        }
      });

      if (result.success) {
        // Create Razorpay linked account for Route payments
        try {
          const { createLinkedAccount } = await import('../services/razorpayRouteService');
          const linkedAccountResult = await createLinkedAccount({
            accountHolderName: bankDetails.accountHolderName.trim(),
            accountNumber: bankDetails.accountNumber.trim(),
            ifscCode: bankDetails.ifscCode.toUpperCase().trim(),
            bankName: bankDetails.bankName.trim(),
            email: bankDetails.email.trim(),
            phone: bankDetails.phone.trim(),
            businessType: bankDetails.businessType,
            businessName: bankDetails.businessName.trim(),
            legalBusinessName: bankDetails.businessName.trim(),
            contactName: bankDetails.accountHolderName.trim(),
            pan: bankDetails.pan.toUpperCase().trim(),
            street1: bankDetails.street1.trim(),
            street2: bankDetails.street2.trim() || 'NA',
            city: bankDetails.city.trim(),
            state: bankDetails.state.trim(),
            pincode: bankDetails.pincode.trim()
          });

          if (linkedAccountResult.success) {
            toast.success('Bank account verified! You can now receive instant payments.');
          } else {
            toast.success('Bank details saved. Payout setup pending.');
          }
        } catch (linkedError) {
          console.error('Linked account creation error:', linkedError);
          toast.success('Bank details saved. Payout verification pending.');
        }
        
        setShowBankDetails(false);
      } else {
        toast.error('Failed to save bank details');
      }
    } catch (error) {
      toast.error('Error saving bank details: ' + error.message);
    } finally {
      setSavingBankDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-3 sm:px-8 pb-3 sm:pb-8 pt-2">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-48 sm:h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-3 sm:px-8 pb-3 sm:pb-8 pt-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-2 sm:mb-4">
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
            Teacher Dashboard
          </h1>
          <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">
            Manage your demo class bookings, hired students, and batches
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-3 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
            <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide -mx-3 sm:mx-0 px-3 sm:px-0">
              <nav className="-mb-px flex space-x-2 sm:space-x-8">
                <button
                  onClick={() => setActiveTab('demo-bookings')}
                  className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'demo-bookings'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Demo ({demoBookings.length})
                </button>
                <button
                  onClick={() => setActiveTab('hired-students')}
                  className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'hired-students'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Students ({hiredStudents.length})
                </button>
                <button
                  onClick={() => setActiveTab('batches')}
                  className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'batches'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Batches ({teacherBatches.length}/{batchLimit})
                </button>
                <button
                  onClick={() => setShowBatchManagement(true)}
                  className="py-2 px-2 sm:px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap flex-shrink-0"
                >
                  <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Manage Batches</span>
                  <span className="sm:hidden">Manage</span>
                </button>
                <button
                  onClick={() => setActiveTab('referrals')}
                  className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'referrals'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline sm:hidden" />
                  <span className="hidden sm:inline">Referrals ({referralStats.totalReferrals})</span>
                  <span className="sm:hidden">Refs</span>
                </button>
                <button
                  onClick={() => setShowBankDetails(!showBankDetails)}
                  className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    showBankDetails
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline sm:hidden" />
                  <span className="hidden sm:inline">Bank Details</span>
                  <span className="sm:hidden">Bank</span>
                </button>
              </nav>
            </div>
            <button
              onClick={loadAllData}
              className="flex items-center justify-center px-3 py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors self-start sm:self-auto"
            >
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-gray-400">Demo Bookings</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{demoBookings.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Users className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-gray-400">Hired Students</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{hiredStudents.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <BookOpen className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-gray-400">Active Batches</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{teacherBatches.length}/{batchLimit}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <IndianRupee className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Earnings</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  ₹{totalEarnings.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Purchase Batches Card - Always Visible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg shadow-sm border border-green-200 dark:border-green-800 p-4 sm:p-6 mb-4 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    Need More Batches?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Purchase 3 additional batches for ₹118 (incl. GST)
                  </p>
                </div>
              </div>
              <div className="ml-0 sm:ml-16 mt-2">
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  Current: <span className="font-semibold">{teacherBatches.length}/{batchLimit} batches</span>
                  {teacherBatches.length < batchLimit && (
                    <span className="ml-2">• {batchLimit - teacherBatches.length} remaining</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowPaymentModal(true);
                // Switch to batches tab if not already there
                if (activeTab !== 'batches') {
                  setActiveTab('batches');
                }
              }}
              className="flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white text-sm sm:text-base rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
            >
              <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              <span>Purchase Batches</span>
              <span className="ml-2 font-semibold">₹118</span>
            </button>
          </div>
        </motion.div>

        {/* Content based on active tab */}
        {activeTab === 'demo-bookings' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Demo Class Bookings</h2>
            </div>
          
            {demoBookings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Time Slots
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                        Booked
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {demoBookings.map((booking) => (
                      <motion.tr
                        key={booking.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div>
                            <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                              {booking.studentName || 'Student'}
                            </div>
                            <div className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 truncate max-w-[120px] sm:max-w-none">
                              {booking.studentEmail}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded text-[10px] sm:text-sm">
                            {booking.subject}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                            {booking.timeSlots?.length || 0} slots
                          </div>
                          {booking.selectedSlot && (
                            <div className="text-[10px] sm:text-xs text-green-600 dark:text-green-400">
                              {formatDateTime(booking.selectedSlot.date, booking.selectedSlot.time)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {getStatusIcon(booking.status)}
                            <span className="ml-1 capitalize hidden sm:inline">{booking.status}</span>
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                          {booking.createdAt ? new Date(booking.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(booking)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Calendar className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No demo bookings yet</h3>
                <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-4">
                  Students will be able to book demo classes with you once you're approved.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Hired Students Tab */}
        {activeTab === 'hired-students' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Hired Students</h2>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Total: {hiredStudents.length} students
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                      Hired Date
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {hiredStudents.map((hiring) => (
                    <motion.tr
                      key={hiring.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div>
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                            {hiring.studentName || 'Student'}
                          </div>
                          <div className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 truncate max-w-[120px] sm:max-w-none">
                            {hiring.studentEmail}
                          </div>
                          <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 sm:hidden">
                            {hiring.createdAt ? new Date(hiring.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded text-[10px] sm:text-sm">
                          {hiring.subject}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                        {hiring.createdAt ? new Date(hiring.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                          Active
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {hiredStudents.length === 0 && (
              <div className="text-center py-8 sm:py-12">
                <Users className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No hired students yet</h3>
                <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-4">
                  Students will appear here once they hire you for classes.
                </p>
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mx-4 sm:mx-auto max-w-md">
                  <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-300">
                    <strong>Debug Info:</strong> Check the browser console for detailed logs about the hiring data.
                  </p>
                  <div className="mt-2 text-[10px] sm:text-xs text-yellow-700 dark:text-yellow-400">
                    <p><strong>Teacher ID:</strong> {user?.uid}</p>
                    <p><strong>Teacher Email:</strong> {user?.email}</p>
                    <p><strong>Teacher Name:</strong> {user?.displayName || 'No display name'}</p>
                    <p className="mt-2 text-yellow-600 dark:text-yellow-500">
                      <strong>Check console for:</strong><br/>
                      • All teacher IDs in hiring records<br/>
                      • Whether your teacher ID matches any hiring records<br/>
                      • Complete hiring data structure
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bank Details Tab */}
        {showBankDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Bank Account Details</h2>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Enter your bank details to receive payments</p>
                </div>
              </div>
            </div>
            
            <div className="p-3 sm:p-6">
              <div className="space-y-4">
                {/* Bank Details Section */}
                <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Bank Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Account Holder Name *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.accountHolderName}
                        onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter account holder name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Account Number *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.accountNumber}
                        onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value.replace(/\D/g, '') })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter account number"
                        maxLength={20}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        IFSC Code *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.ifscCode}
                        onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="ABCD0123456"
                        maxLength={11}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Bank Name *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.bankName}
                        onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter bank name"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Details Section */}
                <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Contact Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={bankDetails.email}
                        onChange={(e) => setBankDetails({ ...bankDetails, email: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter email"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        value={bankDetails.phone}
                        onChange={(e) => setBankDetails({ ...bankDetails, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter 10-digit phone"
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>

                {/* Business Details Section */}
                <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Business Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Business Type *
                      </label>
                      <select
                        value={bankDetails.businessType}
                        onChange={(e) => setBankDetails({ ...bankDetails, businessType: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="individual">Individual</option>
                        <option value="proprietorship">Proprietorship</option>
                        <option value="partnership">Partnership</option>
                        <option value="private_limited">Private Limited</option>
                        <option value="public_limited">Public Limited</option>
                        <option value="llp">LLP</option>
                        <option value="trust">Trust</option>
                        <option value="society">Society</option>
                        <option value="ngo">NGO</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Business/Your Name *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.businessName}
                        onChange={(e) => setBankDetails({ ...bankDetails, businessName: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter your name or business name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        PAN Number *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.pan}
                        onChange={(e) => setBankDetails({ ...bankDetails, pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="ABCDE1234F"
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="pb-4">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Address Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.street1}
                        onChange={(e) => setBankDetails({ ...bankDetails, street1: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter street address"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Street Address Line 2 (Optional)
                      </label>
                      <input
                        type="text"
                        value={bankDetails.street2}
                        onChange={(e) => setBankDetails({ ...bankDetails, street2: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Apartment, suite, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.city}
                        onChange={(e) => setBankDetails({ ...bankDetails, city: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.state}
                        onChange={(e) => setBankDetails({ ...bankDetails, state: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter state"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        value={bankDetails.pincode}
                        onChange={(e) => setBankDetails({ ...bankDetails, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter 6-digit pincode"
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowBankDetails(false);
                      // Reset to saved values
                      if (userProfile?.bankDetails) {
                        setBankDetails({
                          accountHolderName: userProfile.bankDetails.accountHolderName || '',
                          accountNumber: userProfile.bankDetails.accountNumber || '',
                          ifscCode: userProfile.bankDetails.ifscCode || '',
                          bankName: userProfile.bankDetails.bankName || '',
                          email: userProfile.bankDetails.email || userProfile?.email || '',
                          phone: userProfile.bankDetails.phone || userProfile?.phone || '',
                          businessType: userProfile.bankDetails.businessType || 'individual',
                          businessName: userProfile.bankDetails.businessName || userProfile?.displayName || '',
                          pan: userProfile.bankDetails.pan || userProfile?.pan || '',
                          street1: userProfile.bankDetails.street1 || userProfile?.address?.street || '',
                          street2: userProfile.bankDetails.street2 || '',
                          city: userProfile.bankDetails.city || userProfile?.address?.city || '',
                          state: userProfile.bankDetails.state || userProfile?.address?.state || '',
                          pincode: userProfile.bankDetails.pincode || userProfile?.address?.pincode || ''
                        });
                      }
                    }}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveBankDetails}
                    disabled={savingBankDetails}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-1.5 sm:space-x-2"
                  >
                    <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{savingBankDetails ? 'Saving...' : 'Save Details'}</span>
                  </button>
                </div>
              </div>

              {!showBankDetails && bankDetails.accountNumber && (
                <div className="mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Account:</span> {bankDetails.accountNumber.slice(-4).padStart(bankDetails.accountNumber.length, 'X')} | 
                    <span className="font-medium ml-2">IFSC:</span> {bankDetails.ifscCode} | 
                    <span className="font-medium ml-2">Bank:</span> {bankDetails.bankName}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && !showBankDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Referral Program</h2>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Earn ₹10 for each person you refer who registers on our platform
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-6">
              {/* Referral Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800 p-4 sm:p-6"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-4">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Referrals</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                        {referralStats.totalReferrals}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4 sm:p-6"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-4">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Referral Earnings</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                        ₹{referralStats.totalEarnings.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Referral Code Section */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 sm:p-6 mb-6">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                  Your Referral Code
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
                  <div className="flex-1">
                    <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 p-3">
                      <code className="flex-1 text-lg sm:text-xl font-mono font-bold text-gray-900 dark:text-white">
                        {referralCode || 'Loading...'}
                      </code>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={handleCopyReferralCode}
                          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Copy referral code"
                        >
                          <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                          onClick={handleCopyReferralLink}
                          className="p-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Copy referral link"
                        >
                          <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                    {referralLink && (
                      <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-all">
                        {referralLink}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleShareOnWhatsApp}
                    className="flex items-center justify-center px-4 sm:px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span className="text-sm sm:text-base">Share on WhatsApp</span>
                  </button>
                </div>
              </div>

              {/* How It Works */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 sm:p-6 mb-6">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3">
                  How It Works
                </h3>
                <ul className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Share your referral code or link with friends and students</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>When they register as a teacher and enter your code, you earn ₹10</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Earnings are automatically added to your total earnings</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>No limit on referrals - refer as many as you want!</span>
                  </li>
                </ul>
              </div>

              {/* Referrals List */}
              <div>
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    Recent Referrals
                  </h3>
                  <button
                    onClick={loadReferralData}
                    className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center space-x-1"
                    title="Refresh referrals"
                  >
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Refresh</span>
                  </button>
                </div>
                {referrals.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Referred User
                          </th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {referrals.map((referral) => (
                          <motion.tr
                            key={referral.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                              {referral.createdAt?.seconds
                                ? new Date(referral.createdAt.seconds * 1000).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 dark:text-white">
                              {referral.referredUserName || referral.referredUserId?.substring(0, 8) || 'Unknown User'}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
                              ₹{referral.amount}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4">
                              <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                {referral.status || 'Credited'}
                              </span>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12">
                    <Gift className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No referrals yet</h3>
                    <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-4">
                      Start sharing your referral code to earn rewards!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Batches Tab */}
        {activeTab === 'batches' && !showBankDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">My Batches</h2>
                <div className="flex items-center gap-2 sm:gap-3">
                  {teacherBatches.length < batchLimit ? (
                    <button
                      onClick={() => setShowCreateBatch(true)}
                      className="flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                      Create New Batch
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowCreateBatch(true)}
                      disabled
                      className="flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-400 text-white text-xs sm:text-sm rounded-lg cursor-not-allowed opacity-50"
                    >
                      <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                      Limit Reached
                    </button>
                  )}
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white text-xs sm:text-sm rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <IndianRupee className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    <span className="hidden sm:inline">Purchase More Batches</span>
                    <span className="sm:hidden">Buy Batches</span>
                    <span className="ml-1.5 sm:ml-2">(₹118)</span>
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                You can create {batchLimit - teacherBatches.length} more {batchLimit - teacherBatches.length === 1 ? 'batch' : 'batches'} ({teacherBatches.length}/{batchLimit} used)
              </div>
            </div>
            
            {teacherBatches.length >= batchLimit && (
              <div className="px-3 sm:px-6 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Batch Limit Reached
                    </p>
                    <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      You've reached your limit of {batchLimit} batches. Click "Purchase More Batches" above to buy 3 additional batches for ₹118 (incl. GST).
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-3 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {teacherBatches.map((batch) => (
                  <motion.div
                    key={batch.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {batch.batchName}
                    </h3>
                    {batch.description && (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 sm:mb-3">
                        {batch.description}
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                      Created: {batch.createdAt ? new Date(batch.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {batch.studentCount || 0} students
                      </span>
                      <button 
                        onClick={() => setShowBatchManagement(true)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs sm:text-sm flex items-center space-x-1"
                      >
                        <Settings className="w-3 h-3" />
                        <span>Manage</span>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {teacherBatches.length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <BookOpen className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No batches created yet</h3>
                  <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-4">
                    Create your first batch to organize your students.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {showBookingDetails && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Demo Booking Details
                </h2>
                <button
                  onClick={() => setShowBookingDetails(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {/* Student Information */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">Student Information</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {selectedBooking.studentName || 'Student'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {selectedBooking.studentEmail}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Subject</p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                          {selectedBooking.subject}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedBooking.status)}`}>
                          {getStatusIcon(selectedBooking.status)}
                          <span className="ml-1 capitalize">{selectedBooking.status}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">Available Time Slots</h3>
                  <div className="space-y-2 sm:space-y-3">
                    {selectedBooking.timeSlots?.map((slot, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                          <div>
                            <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                              Slot {index + 1}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                              {formatDateTime(slot.date, slot.time)}
                            </p>
                          </div>
                          {selectedBooking.status === 'pending' && (
                            <button
                              onClick={() => handleConfirmBooking(selectedBooking.id, index)}
                              disabled={isProcessing}
                              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white text-xs sm:text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1.5 sm:space-x-2"
                            >
                              <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              <span>Confirm Slot {index + 1}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                {selectedBooking.status === 'pending' && (
                  <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleRejectBooking(selectedBooking.id)}
                      disabled={isProcessing}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white text-xs sm:text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1.5 sm:space-x-2"
                    >
                      <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>Reject Booking</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create Batch Modal */}
      {showCreateBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg max-w-md w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Create New Batch
                </h2>
                <button
                  onClick={() => setShowCreateBatch(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                    Batch Name *
                  </label>
                  <input
                    type="text"
                    value={newBatchName}
                    onChange={(e) => setNewBatchName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter batch name"
                    maxLength={50}
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newBatchDescription}
                    onChange={(e) => setNewBatchDescription(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter batch description"
                    rows={3}
                    maxLength={200}
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5 sm:p-3">
                  <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300">
                    <strong>Note:</strong> You can create up to {batchLimit} batches. Each batch can contain multiple students. 
                    {teacherBatches.length >= batchLimit && ' Purchase more batches to create additional ones.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3 mt-4 sm:mt-6">
                <button
                  onClick={() => setShowCreateBatch(false)}
                  className="px-3 sm:px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBatch}
                  disabled={isProcessing || !newBatchName.trim()}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1.5 sm:space-x-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>Create Batch</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Payment Modal for Batch Purchase */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          paymentData={{
            amount: 118, // ₹100 base + 18% GST = ₹118
            currency: 'INR',
            description: 'Purchase 3 additional batches',
            paymentType: 'batch_addon',
            type: 'batch_addon',
            merchantName: 'Mahstry Private Limited',
            customerName: user?.displayName || user?.email || 'Teacher',
            customerEmail: user?.email || '',
            userId: user?.uid,
            metadata: {
              userId: user?.uid,
              purchaseType: 'additional_batches',
              batchesCount: 3
            }
          }}
          onSuccess={async (response, transactionData) => {
            try {
              // Purchase additional batches
              await teacherHiringService.purchaseAdditionalBatches(user.uid, transactionData?.transactionId || response.razorpay_payment_id);
              
              // Reload batch limit and batches
              await Promise.all([
                loadBatchLimit(),
                loadTeacherBatches()
              ]);
              
              toast.success('Payment successful! You can now create 3 more batches.');
              setShowPaymentModal(false);
            } catch (error) {
              toast.error('Payment successful but failed to update batch limit. Please contact support.');
            }
          }}
          onFailure={(error) => {
            toast.error('Payment failed. Please try again.');
          }}
          title="Purchase Additional Batches"
        />
      )}

      {/* Batch Management Modal - Full Screen */}
      {showBatchManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 z-50 flex items-center justify-center p-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-none w-full h-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 dark:text-white">
                    Batch Management
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                    Manage your batches, content, and students
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBatchManagement(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close batch management"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            {/* Modal Content - Full Height */}
            <div className="flex-1 overflow-hidden">
              <TeacherBatchManagement />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboardMinimal;
